/**
 * Whether the enrolment nudge has already been waved off this sitting (USR-11).
 *
 * **Lifted out of `App.tsx`, unchanged in substance** (ADR-0051). It used to
 * serve `useSteering`, which chose between the app and a routeless interstitial
 * called `SecureSignInPrompt`; the nudge is a second control on the sign-in
 * landing page now, so the flag is read by `Bootstrap` before the app mounts.
 * The `bpf:defer-enrolment` window event went with the hook — there is one
 * reader and it holds the answer in its own state, so a broadcast had nobody to
 * tell.
 *
 * `sessionStorage`, matching ADR-0027's reasoning for the session itself: this
 * is a fact about one sitting at one machine, and it should not follow somebody
 * to the next one. It also means "we will ask again next time you sign in" is
 * true rather than aspirational — a reload inside the same tab does not re-ask,
 * and a fresh sign-in does.
 *
 * Deliberately unlike `passkeyHint.ts`, which is `localStorage` for the opposite
 * reason: that one describes the browser and its whole job is to outlive the tab.
 */
const DEFERRED_KEY = 'bpf.enrolment-deferred';

export function readDeferred(): boolean {
  try {
    return window.sessionStorage.getItem(DEFERRED_KEY) === 'true';
  } catch {
    // A browser refusing storage is not a reason to refuse the app. Asking
    // again on every load is the safe direction to fail in — annoying rather
    // than obstructive, and the answer stays one click away.
    return false;
  }
}

export function deferEnrolment(): void {
  try {
    window.sessionStorage.setItem(DEFERRED_KEY, 'true');
  } catch {
    // Same reasoning as `readDeferred`. The landing page holds the answer in
    // its own state for this sitting, so the nudge still clears under the
    // cursor even where it cannot be remembered.
  }
}
